import { DateTime } from 'luxon';
import { v4 } from 'uuid';

interface GenerateGamesTeam {
  id: string;
  title: string;
  home_primary_color: string | null;
  avatar_id: string | null;
}

interface GameEvent {
  id: string;
  title: string;
  parent_id: string;
  datetime: string;
  location?: string | null;
  home_team_id: string;
  event_teams: {
    data: Array<{
      team_id: string;
    }>;
  };
}

function getShuffledArray<T>(inputArray: T[]): T[] {
  const array = [...inputArray];

  for (let i = array.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }

  return array;
}

function determineHomeTeam(
  team1: GenerateGamesTeam,
  team2: GenerateGamesTeam,
  homeGameCounts: Map<string, number>,
  totalGamesPlayed: Map<string, number>,
): GenerateGamesTeam {
  const team1HomeGames = homeGameCounts.get(team1.id) || 0;
  const team2HomeGames = homeGameCounts.get(team2.id) || 0;
  const team1TotalGames = totalGamesPlayed.get(team1.id) || 0;
  const team2TotalGames = totalGamesPlayed.get(team2.id) || 0;

  const team1HomePercentage =
    team1TotalGames > 0 ? team1HomeGames / team1TotalGames : 0;
  const team2HomePercentage =
    team2TotalGames > 0 ? team2HomeGames / team2TotalGames : 0;

  if (team1HomePercentage < team2HomePercentage) {
    return team1;
  }
  if (team2HomePercentage < team1HomePercentage) {
    return team2;
  }

  if (team1HomeGames < team2HomeGames) {
    return team1;
  }
  if (team2HomeGames < team1HomeGames) {
    return team2;
  }

  return team1.id < team2.id ? team1 : team2;
}

function generateAllPairs(
  teams: GenerateGamesTeam[],
): Array<[GenerateGamesTeam, GenerateGamesTeam]> {
  const pairs: Array<[GenerateGamesTeam, GenerateGamesTeam]> = [];
  const teamCount = teams.length;

  for (let i = 0; i < teamCount; i += 1) {
    for (let j = i + 1; j < teamCount; j += 1) {
      pairs.push([teams[i], teams[j]]);
    }
  }

  return pairs;
}

function calculateGameDateTime(
  baseDateTime: string,
  weekNumber: number,
  repeatEvery: { days?: number; weeks?: number; months?: number },
): string {
  return DateTime.fromISO(baseDateTime)
    .plus({
      weeks: weekNumber * (repeatEvery.weeks || 0),
      days: weekNumber * (repeatEvery.days || 0),
      months: weekNumber * (repeatEvery.months || 0),
    })
    .toUTC()
    .toISO()!;
}

function createGameEvent(
  homeTeam: GenerateGamesTeam,
  awayTeam: GenerateGamesTeam,
  weekNumber: number,
  gameSlot: { datetime: string; location?: string | null },
  parentId: string,
  repeatEvery: { days?: number; weeks?: number; months?: number },
): GameEvent {
  return {
    id: v4(),
    title: `Week ${weekNumber + 1}`,
    parent_id: parentId,
    datetime: calculateGameDateTime(gameSlot.datetime, weekNumber, repeatEvery),
    location: gameSlot.location,
    home_team_id: homeTeam.id,
    event_teams: {
      data: [
        {
          team_id: homeTeam.id,
        },
        {
          team_id: awayTeam.id,
        },
      ],
    },
  };
}

function updateGameCounters(
  homeTeam: GenerateGamesTeam,
  awayTeam: GenerateGamesTeam,
  homeGameCounts: Map<string, number>,
  totalGamesPlayed: Map<string, number>,
): void {
  homeGameCounts.set(homeTeam.id, (homeGameCounts.get(homeTeam.id) || 0) + 1);
  totalGamesPlayed.set(
    homeTeam.id,
    (totalGamesPlayed.get(homeTeam.id) || 0) + 1,
  );
  totalGamesPlayed.set(
    awayTeam.id,
    (totalGamesPlayed.get(awayTeam.id) || 0) + 1,
  );
}

function scheduleGame(
  teamA: GenerateGamesTeam,
  teamB: GenerateGamesTeam,
  weekNumber: number,
  gameIndex: number,
  initialGames: { datetime: string; location?: string | null }[],
  parentId: string,
  repeatEvery: { days?: number; weeks?: number; months?: number },
  homeGameCounts: Map<string, number>,
  totalGamesPlayed: Map<string, number>,
  events: GameEvent[],
): boolean {
  if (gameIndex >= initialGames.length) {
    return false;
  }

  const homeTeam = determineHomeTeam(
    teamA,
    teamB,
    homeGameCounts,
    totalGamesPlayed,
  );
  const awayTeam = homeTeam === teamA ? teamB : teamA;

  const event = createGameEvent(
    homeTeam,
    awayTeam,
    weekNumber,
    initialGames[gameIndex],
    parentId,
    repeatEvery,
  );

  events.push(event);

  updateGameCounters(homeTeam, awayTeam, homeGameCounts, totalGamesPlayed);

  return true;
}

function scheduleEvenTeams(
  teams: GenerateGamesTeam[],
  matchup: number,
  initialGames: { datetime: string; location?: string | null }[],
  parentId: string,
  repeatEvery: { days?: number; weeks?: number; months?: number },
  homeGameCounts: Map<string, number>,
  totalGamesPlayed: Map<string, number>,
  events: GameEvent[],
) {
  const numberOfTeams = teams.length;
  const numberOfWeeks = numberOfTeams - 1;
  const teamPool = getShuffledArray(teams);

  for (let week = 0; week < numberOfWeeks; week += 1) {
    let gameIndex = 0;

    for (let i = 0; i < numberOfTeams / 2; i += 1) {
      const teamA = teamPool[i];
      const teamB = teamPool[numberOfTeams - 1 - i];
      const weekNumber = week + numberOfWeeks * matchup;

      const gameScheduled = scheduleGame(
        teamA,
        teamB,
        weekNumber,
        gameIndex,
        initialGames,
        parentId,
        repeatEvery,
        homeGameCounts,
        totalGamesPlayed,
        events,
      );

      if (!gameScheduled) {
        break;
      }

      gameIndex += 1;
    }

    const lastTeam = teamPool.pop();
    if (lastTeam) {
      teamPool.splice(1, 0, lastTeam);
    }
  }
}

function scheduleOddTeamsWithByes(
  teams: GenerateGamesTeam[],
  matchup: number,
  initialGames: { datetime: string; location?: string | null }[],
  parentId: string,
  repeatEvery: { days?: number; weeks?: number; months?: number },
  homeGameCounts: Map<string, number>,
  totalGamesPlayed: Map<string, number>,
  events: GameEvent[],
) {
  const numberOfTeams = teams.length;
  const numberOfWeeks = numberOfTeams;
  const allPairs = generateAllPairs(teams);

  for (let week = 0; week < numberOfWeeks && allPairs.length > 0; week += 1) {
    const weekNumber = week + numberOfWeeks * matchup;
    const usedTeams = new Set<string>();
    let gameIndex = 0;

    for (
      let pairIndex = allPairs.length - 1;
      pairIndex >= 0 && gameIndex < initialGames.length;
      pairIndex -= 1
    ) {
      const [teamA, teamB] = allPairs[pairIndex];

      if (!usedTeams.has(teamA.id) && !usedTeams.has(teamB.id)) {
        const gameScheduled = scheduleGame(
          teamA,
          teamB,
          weekNumber,
          gameIndex,
          initialGames,
          parentId,
          repeatEvery,
          homeGameCounts,
          totalGamesPlayed,
          events,
        );

        if (gameScheduled) {
          usedTeams.add(teamA.id);
          usedTeams.add(teamB.id);
          allPairs.splice(pairIndex, 1);
          gameIndex += 1;
        }
      }
    }
  }
}

function scheduleOddTeamsWithoutByes(
  teams: GenerateGamesTeam[],
  matchup: number,
  initialGames: { datetime: string; location?: string | null }[],
  parentId: string,
  repeatEvery: { days?: number; weeks?: number; months?: number },
  homeGameCounts: Map<string, number>,
  totalGamesPlayed: Map<string, number>,
  events: GameEvent[],
) {
  const numberOfTeams = teams.length;
  const numberOfWeeks = numberOfTeams - 1;
  const allPairs = generateAllPairs(teams);

  for (let week = 0; week < numberOfWeeks && allPairs.length > 0; week += 1) {
    const weekNumber = week + numberOfWeeks * matchup;
    let gameIndex = 0;

    while (gameIndex < initialGames.length && allPairs.length > 0) {
      const pair = allPairs.pop()!;
      const [teamA, teamB] = pair;

      const gameScheduled = scheduleGame(
        teamA,
        teamB,
        weekNumber,
        gameIndex,
        initialGames,
        parentId,
        repeatEvery,
        homeGameCounts,
        totalGamesPlayed,
        events,
      );

      if (!gameScheduled) {
        allPairs.push(pair);
        break;
      }

      gameIndex += 1;
    }
  }
}

export default function generateGames({
  teams,
  initialGames,
  numberOfMatchups,
  parentId,
  repeatEvery = { weeks: 1 },
  byeWeeks = true,
}: {
  teams: GenerateGamesTeam[];
  initialGames: { datetime: string; location?: string | null }[];
  numberOfMatchups: number;
  parentId: string;
  repeatEvery: { days?: number; weeks?: number; months?: number };
  byeWeeks?: boolean;
}) {
  const numberOfTeams = teams.length;

  if (numberOfTeams < 2) {
    return {
      games: [],
      expectedInitialGameCount: 0,
    };
  }

  const events: GameEvent[] = [];
  const homeGameCounts = new Map<string, number>();
  const totalGamesPlayed = new Map<string, number>();

  teams.forEach((team) => {
    homeGameCounts.set(team.id, 0);
    totalGamesPlayed.set(team.id, 0);
  });

  for (let matchup = 0; matchup < numberOfMatchups; matchup += 1) {
    const isEven = numberOfTeams % 2 === 0;

    if (isEven) {
      scheduleEvenTeams(
        teams,
        matchup,
        initialGames,
        parentId,
        repeatEvery,
        homeGameCounts,
        totalGamesPlayed,
        events,
      );
    } else if (byeWeeks) {
      scheduleOddTeamsWithByes(
        teams,
        matchup,
        initialGames,
        parentId,
        repeatEvery,
        homeGameCounts,
        totalGamesPlayed,
        events,
      );
    } else {
      scheduleOddTeamsWithoutByes(
        teams,
        matchup,
        initialGames,
        parentId,
        repeatEvery,
        homeGameCounts,
        totalGamesPlayed,
        events,
      );
    }
  }

  const totalGamesPerMatchup = (numberOfTeams * (numberOfTeams - 1)) / 2;
  let expectedInitialGameCount: number;

  if (numberOfTeams % 2 === 0) {
    expectedInitialGameCount = numberOfTeams / 2;
  } else {
    if (byeWeeks) {
      expectedInitialGameCount = Math.floor((numberOfTeams - 1) / 2);
    } else {
      const numberOfWeeks = numberOfTeams - 1;
      expectedInitialGameCount = Math.ceil(
        totalGamesPerMatchup / numberOfWeeks,
      );
    }
  }

  return {
    games: events,
    expectedInitialGameCount,
  };
}
